import { normalizeDoc } from './normalize.js';
import { createSegmenter } from './segmenter.js';
import { baseLang, languagePack } from './lang/index.js';
const LETTER_OR_NUMBER = /[\p{L}\p{N}]/u;
const LETTER = /\p{L}/u;
const UPPERCASE = /\p{Lu}/u;
const LOWERCASE = /\p{Ll}/u;
const NUMBER = /\p{N}/u;
/**
 * Punctuation that reads as ordinary text furniture. Any cluster that is
 * neither letter/number nor in this set (emoji, math, currency, …) puts the
 * token in the digit-or-symbol timing class. Deliberately Unicode-wide —
 * the prototype's Latin-1-bounded class regex mistimed Turkish text (PD-3).
 */
const PLAIN_PUNCT = /^[.,!?;:'"()[\]{}«»„“”‘’‹›\-–—…·¿¡]+$/u;
const HYPHEN = '-';
/** Hyphenated tokens longer than this many clusters split at each hyphen (PRD §7.2). */
export const HYPHEN_SPLIT_MIN_CLUSTERS = 6;
/**
 * ICU sentence segmentation breaks after abbreviations ("Dr. ", "T.C. ").
 * Breaks are suppressed when the preceding word is a known abbreviation or a
 * letter-dot run. Minimal en/tr lists for P0 (languages beyond en+tr are a
 * non-goal); tokenizer-owned because language packs carry word lists only.
 */
const SENTENCE_ABBREVIATIONS = new Map([
    [
        'en',
        new Set([
            'dr.',
            'mr.',
            'mrs.',
            'ms.',
            'prof.',
            'sr.',
            'jr.',
            'st.',
            'vs.',
            'etc.',
            'e.g.',
            'i.e.',
            'no.',
            'fig.',
            'al.',
        ]),
    ],
    [
        'tr',
        new Set([
            'dr.',
            'prof.',
            'doç.',
            'yrd.',
            'av.',
            'sn.',
            'vb.',
            'vs.',
            'bkz.',
            'sf.',
            'no.',
            'örn.',
        ]),
    ],
]);
/** Letter-dot runs like T.C., A.B.D., U.S. never end a sentence. */
const LETTER_DOT_RUN = /^(\p{L}\.)+$/u;
const NO_ABBREVIATIONS = new Set();
/** F3: abbreviation lookup canonicalizes like pack lookup — exact tag, then base subtag, then empty. */
function abbreviationsFor(lang) {
    return (SENTENCE_ABBREVIATIONS.get(lang) ??
        SENTENCE_ABBREVIATIONS.get(baseLang(lang)) ??
        NO_ABBREVIATIONS);
}
/**
 * Chapter index per block, derived purely from Block metadata (PD-12) —
 * never from character offsets. Every 'chapter-heading' block starts the
 * next chapter; a heading at block 0 opens chapter 0 (no empty preamble).
 */
export function chapterIndexByBlock(blocks) {
    const out = [];
    let current = 0;
    blocks.forEach((block, i) => {
        if (block.kind === 'chapter-heading' && i > 0)
            current += 1;
        out.push(current);
    });
    return out;
}
/** The letter/number core of a token, sliced at grapheme-cluster boundaries. */
export function coreText(token) {
    if (token.coreLen === 0)
        return '';
    const start = token.clusterToUnitOffsets[token.coreStart];
    const end = token.clusterToUnitOffsets[token.coreStart + token.coreLen];
    if (start === undefined || end === undefined)
        return '';
    return token.raw.slice(start, end);
}
/**
 * F1: tokens are DISPLAY units assembled from ICU word segmentation, not raw
 * ICU segments. Whitespace segments are dropped; contiguous segments form a
 * run. Within a run, punctuation/symbol segments attach to a neighboring
 * word by adjacency: after a word → trailing on it (adjacent to both sides,
 * e.g. "hello,world", resolves to trailing on the previous token); before
 * any word → leading on the next; a run with no word segment at all becomes
 * a standalone punct token (centered ORP). A single '-' directly between two
 * words joins them into one assembled compound so the >6-cluster hyphen
 * split sees "well-known" whole, exactly as before.
 */
function assembleDisplayTokens(text, seg) {
    const retained = seg.words(text).filter((s) => !/^\s+$/u.test(s.segment));
    const out = [];
    let run = [];
    const flushRun = () => {
        const first = run[0];
        const last = run[run.length - 1];
        if (first === undefined || last === undefined) {
            run = [];
            return;
        }
        const runStart = first.index;
        const runEnd = last.index + last.segment.length;
        // Word groups; a lone '-' with a word immediately on both sides joins.
        const groups = [];
        for (let i = 0; i < run.length; i++) {
            const s = run[i];
            if (s === undefined || s.isWordLike !== true)
                continue;
            const prev = groups[groups.length - 1];
            const between = run[i - 1];
            if (prev !== undefined &&
                between !== undefined &&
                between.segment === HYPHEN &&
                between.index === prev.end &&
                s.index === between.index + between.segment.length) {
                prev.end = s.index + s.segment.length;
            }
            else {
                groups.push({ start: s.index, end: s.index + s.segment.length });
            }
        }
        if (groups.length === 0) {
            out.push({ raw: text.slice(runStart, runEnd), startUnit: runStart });
        }
        else {
            groups.forEach((group, k) => {
                const start = k === 0 ? runStart : group.start;
                const nextGroup = groups[k + 1];
                const end = nextGroup === undefined ? runEnd : nextGroup.start;
                out.push({ raw: text.slice(start, end), startUnit: start });
            });
        }
        run = [];
    };
    let prevEnd = -1;
    for (const segment of retained) {
        if (run.length > 0 && segment.index !== prevEnd)
            flushRun();
        run.push(segment);
        prevEnd = segment.index + segment.segment.length;
    }
    flushRun();
    return out;
}
export function tokenize(doc) {
    const normalized = normalizeDoc(doc);
    const lang = normalized.lang;
    const seg = createSegmenter(lang);
    const pack = languagePack(lang);
    const highFreq = new Set(pack.highFrequency.map((w) => w.toLocaleLowerCase(lang)));
    const highFreqGate = pack.highFreqGateClusters;
    const chapterByBlock = chapterIndexByBlock(normalized.blocks);
    const drafts = [];
    let sentenceBase = 0;
    normalized.blocks.forEach((block, blockIdx) => {
        const spans = sentenceSpans(block.text, seg, lang);
        let spanPtr = 0;
        for (const displayToken of assembleDisplayTokens(block.text, seg)) {
            for (const part of splitHyphenated(displayToken.raw, seg)) {
                const partStart = displayToken.startUnit + part.unitOffset;
                while (spanPtr < spans.length - 1) {
                    const span = spans[spanPtr];
                    if (span !== undefined && partStart >= span.end)
                        spanPtr += 1;
                    else
                        break;
                }
                drafts.push(buildDraft(part.text, seg, lang, highFreq, highFreqGate, blockIdx, sentenceBase + spanPtr));
            }
        }
        sentenceBase += spans.length;
    });
    return drafts.map((draft, i) => {
        const next = drafts[i + 1];
        const flags = {
            ...draft.flags,
            endsSentence: next === undefined || next.token.sentenceIdx !== draft.token.sentenceIdx,
            endsParagraph: next === undefined || next.token.blockIdx !== draft.token.blockIdx,
            endsChapter: next === undefined ||
                chapterByBlock[next.token.blockIdx] !== chapterByBlock[draft.token.blockIdx],
        };
        return { ...draft.token, flags };
    });
}
function buildDraft(raw, seg, lang, highFreq, highFreqGate, blockIdx, sentenceIdx) {
    const clusters = seg.graphemes(raw);
    const clusterToUnitOffsets = clusters.map((c) => c.index);
    clusterToUnitOffsets.push(raw.length);
    let first = -1;
    let last = -1;
    let letters = 0;
    let upper = 0;
    let lower = 0;
    let hasNum = false;
    let hasSym = false;
    clusters.forEach((cluster, i) => {
        const s = cluster.segment;
        if (LETTER_OR_NUMBER.test(s)) {
            if (first < 0)
                first = i;
            last = i;
        }
        if (LETTER.test(s)) {
            letters += 1;
            if (UPPERCASE.test(s))
                upper += 1;
            if (LOWERCASE.test(s))
                lower += 1;
        }
        else if (NUMBER.test(s)) {
            hasNum = true;
        }
        else if (!PLAIN_PUNCT.test(s)) {
            hasSym = true;
        }
    });
    const isPurePunct = first < 0;
    const coreStart = isPurePunct ? 0 : first;
    const coreLen = isPurePunct ? 0 : last - first + 1;
    const coreStartUnit = clusterToUnitOffsets[coreStart];
    const coreEndUnit = clusterToUnitOffsets[coreStart + coreLen];
    const core = isPurePunct || coreStartUnit === undefined || coreEndUnit === undefined
        ? ''
        : raw.slice(coreStartUnit, coreEndUnit);
    return {
        token: { raw, coreStart, coreLen, sentenceIdx, blockIdx, clusterToUnitOffsets },
        flags: {
            // 0.7× gate: list membership AND within the pack's cluster gate (null = ungated).
            isHighFreq: !isPurePunct &&
                highFreq.has(core.toLocaleLowerCase(lang)) &&
                (highFreqGate === null || coreLen <= highFreqGate),
            isCapsAcronym: letters >= 2 && lower === 0 && upper === letters,
            hasDigitOrSymbol: hasNum || hasSym,
            isPurePunct,
        },
    };
}
/**
 * Tokens longer than HYPHEN_SPLIT_MIN_CLUSTERS clusters split after each
 * hyphen; the hyphen stays attached to the leading part ("well-known" →
 * "well-", "known"). Behavior port of the prototype, cluster-counted.
 */
function splitHyphenated(word, seg) {
    const clusters = seg.graphemes(word);
    if (clusters.length <= HYPHEN_SPLIT_MIN_CLUSTERS || !clusters.some((c) => c.segment === HYPHEN)) {
        return [{ text: word, unitOffset: 0 }];
    }
    const parts = [];
    let partStartUnit = 0;
    for (const cluster of clusters) {
        if (cluster.segment === HYPHEN) {
            const endUnit = cluster.index + cluster.segment.length;
            parts.push({ text: word.slice(partStartUnit, endUnit), unitOffset: partStartUnit });
            partStartUnit = endUnit;
        }
    }
    if (partStartUnit < word.length) {
        parts.push({ text: word.slice(partStartUnit), unitOffset: partStartUnit });
    }
    return parts.filter((p) => p.text !== HYPHEN);
}
function sentenceSpans(text, seg, lang) {
    const raw = seg.sentences(text);
    if (raw.length === 0)
        return [{ start: 0, end: text.length }];
    const abbreviations = abbreviationsFor(lang);
    const merged = [];
    for (const span of raw) {
        const start = span.index;
        const end = span.index + span.segment.length;
        const prev = merged[merged.length - 1];
        if (prev !== undefined &&
            suppressBreak(text.slice(prev.start, prev.end), abbreviations, lang)) {
            prev.end = end;
        }
        else {
            merged.push({ start, end });
        }
    }
    return merged;
}
function suppressBreak(precedingText, abbreviations, lang) {
    const match = /(\S+)\s*$/u.exec(precedingText);
    const word = match?.[1];
    if (word === undefined)
        return false;
    return abbreviations.has(word.toLocaleLowerCase(lang)) || LETTER_DOT_RUN.test(word);
}
//# sourceMappingURL=tokenizer.js.map